/**
 * ContentCalendar30 — Panel de contenido 30 días
 * Sube vídeos/imágenes, genera captions con IA, programa publicaciones
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Upload, Calendar, Video, Image, Sparkles, Trash2, Edit3,
  CheckCircle2, Clock, Send, RefreshCw, X, Play, Hash,
  Instagram, Music2, Globe, ChevronLeft, ChevronRight,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHANNELS = [
  { id: 'tiktok', label: 'TikTok', icon: Music2, color: '#fe2c55' },
  { id: 'instagram', label: 'Instagram', icon: Instagram, color: '#E1306C' },
  { id: 'facebook', label: 'Facebook', icon: Globe, color: '#1877F2' },
];

const STATUS_STYLE = {
  scheduled: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  published: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  draft: 'bg-slate-700/50 text-slate-400 border-slate-600/30',
};

function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year, month) {
  return new Date(year, month, 1).getDay();
}

function pad(n) { return String(n).padStart(2, '0'); }

async function jFetch(authFetch, url, opts = {}) {
  const isForm = opts.body instanceof FormData;
  const headers = isForm ? {} : { 'Content-Type': 'application/json', ...(opts.headers || {}) };
  const res = await authFetch(url, { ...opts, headers });
  const json = await res.json();
  if (!res.ok) throw new Error(json?.detail || 'Error del servidor');
  return json;
}

export default function ContentCalendar30({ authFetch }) {
  const fileRef = useRef();
  const today = new Date();
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadModal, setUploadModal] = useState(false);
  const [editPost, setEditPost] = useState(null);
  const [genLoading, setGenLoading] = useState(false);
  const [toast, setToast] = useState('');

  const [form, setForm] = useState({
    file: null, title: '', caption: '', scheduled_date: '', post_type: 'video',
    channels: ['tiktok', 'instagram', 'facebook'], tags: '',
  });

  const showToast = (m) => { setToast(m); setTimeout(() => setToast(''), 3500); };

  const loadPosts = useCallback(async () => {
    setLoading(true);
    try {
      const monthStr = `${viewYear}-${pad(viewMonth + 1)}`;
      const d = await jFetch(authFetch, `${API}/admin/content/calendar?month=${monthStr}`);
      setPosts(d.posts || []);
    } catch (e) { showToast('Error cargando calendario'); }
    finally { setLoading(false); }
  }, [authFetch, viewYear, viewMonth]);

  useEffect(() => { loadPosts(); }, [loadPosts]);

  const generateCaption = async () => {
    setGenLoading(true);
    try {
      const d = await jFetch(authFetch, `${API}/admin/content/generate-caption`, {
        method: 'POST',
        body: JSON.stringify({ context: form.title || 'contenido para Neuralia369', channels: form.channels }),
      });
      const r = d.result;
      setForm(f => ({
        ...f,
        caption: r.caption + '\n\n' + (r.hashtags || []).join(' '),
      }));
      showToast('Caption generado con IA');
    } catch (e) { showToast('Error generando caption'); }
    finally { setGenLoading(false); }
  };

  const uploadPost = async () => {
    if (!form.file) return;
    const fd = new FormData();
    fd.append('file', form.file);
    fd.append('title', form.title);
    fd.append('caption', form.caption);
    fd.append('scheduled_date', form.scheduled_date || `${viewYear}-${pad(viewMonth + 1)}-01`);
    fd.append('channels', form.channels.join(','));
    fd.append('post_type', form.post_type);
    fd.append('tags', form.tags);
    try {
      await jFetch(authFetch, `${API}/admin/content/upload`, { method: 'POST', body: fd });
      showToast('Vídeo/Imagen subido al calendario');
      setUploadModal(false);
      setForm({ file: null, title: '', caption: '', scheduled_date: '', post_type: 'video', channels: ['tiktok', 'instagram', 'facebook'], tags: '' });
      loadPosts();
    } catch (e) { showToast(`Error: ${e.message}`); }
  };

  const deletePost = async (post_id) => {
    if (!window.confirm('¿Eliminar este post?')) return;
    await jFetch(authFetch, `${API}/admin/content/posts/${post_id}`, { method: 'DELETE' });
    showToast('Post eliminado');
    loadPosts();
  };

  const updatePost = async (post_id, body) => {
    await jFetch(authFetch, `${API}/admin/content/posts/${post_id}`, { method: 'PATCH', body: JSON.stringify(body) });
    showToast('Post actualizado');
    setEditPost(null);
    loadPosts();
  };

  const toggleChannel = (ch) => {
    setForm(f => ({
      ...f,
      channels: f.channels.includes(ch) ? f.channels.filter(c => c !== ch) : [...f.channels, ch],
    }));
  };

  // Calendario
  const daysInMonth = getDaysInMonth(viewYear, viewMonth);
  const firstDay = getFirstDayOfMonth(viewYear, viewMonth);
  const monthStr = `${viewYear}-${pad(viewMonth + 1)}`;
  const postsByDay = {};
  posts.forEach(p => {
    const day = parseInt((p.scheduled_date || '').split('-')[2] || '0', 10);
    if (!postsByDay[day]) postsByDay[day] = [];
    postsByDay[day].push(p);
  });

  const monthNames = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-white">Calendario de Contenido 30 Días</h2>
          <p className="text-slate-400 text-sm mt-1">
            {posts.length} posts programados · Sube vídeos e imágenes, genera captions con IA
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={loadPosts} className="p-2 bg-slate-800 border border-slate-700 text-slate-400 rounded-xl hover:bg-slate-700 transition-colors">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            data-testid="upload-content-btn"
            onClick={() => setUploadModal(true)}
            className="flex items-center gap-2 px-4 py-2.5 bg-[#00d4ff] text-[#030712] font-bold rounded-xl hover:bg-[#00bfea] transition-colors"
          >
            <Upload className="w-4 h-4" /> Subir Vídeo / Imagen
          </button>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex items-center gap-4">
        <button onClick={() => { if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); } else setViewMonth(m => m - 1); }}
          className="p-2 bg-slate-800 border border-slate-700 rounded-xl text-slate-400 hover:bg-slate-700 transition-colors">
          <ChevronLeft className="w-4 h-4" />
        </button>
        <span className="text-white font-bold text-lg">{monthNames[viewMonth]} {viewYear}</span>
        <button onClick={() => { if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); } else setViewMonth(m => m + 1); }}
          className="p-2 bg-slate-800 border border-slate-700 rounded-xl text-slate-400 hover:bg-slate-700 transition-colors">
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      {/* Calendar grid */}
      <div className="bg-slate-900/50 border border-slate-800 rounded-2xl overflow-hidden">
        <div className="grid grid-cols-7 border-b border-slate-800">
          {['Dom','Lun','Mar','Mié','Jue','Vie','Sáb'].map(d => (
            <div key={d} className="py-2 text-center text-xs font-medium text-slate-500">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {/* Empty cells */}
          {Array.from({ length: firstDay }).map((_, i) => (
            <div key={`empty-${i}`} className="h-20 border-b border-r border-slate-800/50" />
          ))}
          {/* Day cells */}
          {Array.from({ length: daysInMonth }).map((_, i) => {
            const day = i + 1;
            const dayPosts = postsByDay[day] || [];
            const isToday = today.getFullYear() === viewYear && today.getMonth() === viewMonth && today.getDate() === day;
            return (
              <div
                key={day}
                className={`h-20 border-b border-r border-slate-800/50 p-1 cursor-pointer hover:bg-slate-800/30 transition-colors ${isToday ? 'bg-[#00d4ff]/5' : ''}`}
                onClick={() => {
                  setForm(f => ({ ...f, scheduled_date: `${monthStr}-${pad(day)}` }));
                  setUploadModal(true);
                }}
              >
                <div className={`text-xs font-bold mb-1 ${isToday ? 'text-[#00d4ff]' : 'text-slate-500'}`}>{day}</div>
                <div className="space-y-0.5 overflow-hidden">
                  {dayPosts.slice(0, 2).map(p => (
                    <div
                      key={p.post_id}
                      onClick={e => { e.stopPropagation(); setEditPost(p); }}
                      className="text-[10px] px-1 py-0.5 rounded bg-[#00d4ff]/15 text-[#00d4ff] truncate leading-tight"
                    >
                      {p.post_type === 'video' ? '▶ ' : '🖼 '}{p.title || 'Post'}
                    </div>
                  ))}
                  {dayPosts.length > 2 && (
                    <div className="text-[10px] text-slate-500">+{dayPosts.length - 2} más</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Posts list */}
      {posts.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-lg font-bold text-white">Posts de {monthNames[viewMonth]}</h3>
          {posts.map(p => (
            <div key={p.post_id} className="bg-slate-900/50 border border-slate-800 rounded-xl p-4 flex items-center gap-4 hover:border-slate-700 transition-colors">
              {/* Thumbnail */}
              <div className="w-14 h-14 rounded-xl bg-slate-800 border border-slate-700 flex items-center justify-center flex-shrink-0 overflow-hidden">
                {p.public_url && p.post_type === 'image' ? (
                  <img src={p.public_url} alt="" className="w-full h-full object-cover" />
                ) : p.post_type === 'video' ? (
                  <Play className="w-6 h-6 text-slate-500" />
                ) : (
                  <Image className="w-6 h-6 text-slate-500" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-white text-sm truncate">{p.title || p.file_name}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-lg border ${STATUS_STYLE[p.status] || STATUS_STYLE.draft}`}>
                    {p.status}
                  </span>
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <Calendar className="w-3.5 h-3.5 text-slate-500" />
                  <span className="text-slate-500 text-xs">{p.scheduled_date}</span>
                  <div className="flex gap-1 ml-1">
                    {(p.channels || []).map(ch => {
                      const cfg = CHANNELS.find(c => c.id === ch);
                      if (!cfg) return null;
                      const Icon = cfg.icon;
                      return <Icon key={ch} className="w-3.5 h-3.5" style={{ color: cfg.color }} />;
                    })}
                  </div>
                </div>
                {p.caption && (
                  <p className="text-slate-600 text-xs mt-1 truncate">{p.caption}</p>
                )}
              </div>
              <div className="flex gap-2 flex-shrink-0">
                <button onClick={() => setEditPost(p)}
                  className="p-1.5 bg-slate-800 border border-slate-700 text-slate-400 rounded-lg hover:bg-slate-700 transition-colors">
                  <Edit3 className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => deletePost(p.post_id)}
                  className="p-1.5 bg-red-500/10 border border-red-500/20 text-red-400 rounded-lg hover:bg-red-500/20 transition-colors">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Upload Modal */}
      {uploadModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-[#0f1117] border border-slate-800 rounded-2xl w-full max-w-xl shadow-2xl my-4">
            <div className="flex items-center justify-between p-5 border-b border-slate-800">
              <h3 className="font-bold text-white text-lg">Subir contenido</h3>
              <button onClick={() => setUploadModal(false)} className="p-1.5 text-slate-500 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              {/* File picker */}
              <div
                onClick={() => fileRef.current?.click()}
                className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors ${
                  form.file ? 'border-[#00d4ff] bg-[#00d4ff]/5' : 'border-slate-700 hover:border-slate-600'
                }`}
              >
                <input ref={fileRef} type="file"
                  accept="video/mp4,video/mov,video/avi,video/quicktime,image/jpeg,image/png,image/webp"
                  className="hidden"
                  onChange={e => {
                    const f = e.target.files[0];
                    if (f) {
                      setForm(prev => ({
                        ...prev, file: f, title: prev.title || f.name.replace(/\.[^/.]+$/, ''),
                        post_type: f.type.startsWith('video') ? 'video' : 'image',
                      }));
                    }
                  }}
                />
                {form.file ? (
                  <div className="flex items-center justify-center gap-3">
                    {form.post_type === 'video' ? <Video className="w-7 h-7 text-[#00d4ff]" /> : <Image className="w-7 h-7 text-[#00d4ff]" />}
                    <div className="text-left">
                      <p className="text-white font-medium text-sm">{form.file.name}</p>
                      <p className="text-slate-500 text-xs">{(form.file.size / 1024 / 1024).toFixed(1)} MB</p>
                    </div>
                  </div>
                ) : (
                  <>
                    <Upload className="w-8 h-8 text-slate-600 mx-auto mb-2" />
                    <p className="text-slate-300 text-sm font-medium">Arrastra o haz clic</p>
                    <p className="text-slate-600 text-xs mt-1">MP4, MOV, JPG, PNG · Máx 500MB</p>
                  </>
                )}
              </div>

              {/* Title */}
              <input type="text" placeholder="Título del post"
                value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                className="w-full bg-slate-800 border border-slate-700 text-white rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-[#00d4ff]/50"
              />

              {/* Caption + AI generate */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-medium text-slate-400">Caption</label>
                  <button
                    data-testid="generate-caption-btn"
                    onClick={generateCaption}
                    disabled={genLoading}
                    className="flex items-center gap-1.5 text-xs px-3 py-1.5 bg-purple-500/15 border border-purple-500/30 text-purple-400 rounded-lg hover:bg-purple-500/25 transition-colors disabled:opacity-50"
                  >
                    <Sparkles className="w-3.5 h-3.5" />
                    {genLoading ? 'Generando...' : 'Generar con IA'}
                  </button>
                </div>
                <textarea
                  placeholder="Escribe el caption o genera uno con IA..."
                  value={form.caption}
                  onChange={e => setForm(f => ({ ...f, caption: e.target.value }))}
                  rows={4}
                  className="w-full bg-slate-800 border border-slate-700 text-white rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-[#00d4ff]/50 resize-none"
                />
              </div>

              {/* Date + channels */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-slate-400 block mb-1">Fecha programada</label>
                  <input type="date" value={form.scheduled_date}
                    onChange={e => setForm(f => ({ ...f, scheduled_date: e.target.value }))}
                    className="w-full bg-slate-800 border border-slate-700 text-white rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#00d4ff]/50"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-400 block mb-1">Tags</label>
                  <input type="text" placeholder="lanzamiento, demo..." value={form.tags}
                    onChange={e => setForm(f => ({ ...f, tags: e.target.value }))}
                    className="w-full bg-slate-800 border border-slate-700 text-white rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#00d4ff]/50"
                  />
                </div>
              </div>

              {/* Channels */}
              <div>
                <label className="text-xs text-slate-400 block mb-2">Publicar en</label>
                <div className="flex gap-2">
                  {CHANNELS.map(ch => {
                    const Icon = ch.icon;
                    const active = form.channels.includes(ch.id);
                    return (
                      <button key={ch.id} onClick={() => toggleChannel(ch.id)}
                        className={`flex items-center gap-1.5 px-3 py-2 rounded-xl border text-xs font-medium transition-all ${
                          active ? 'text-white' : 'bg-slate-800 border-slate-700 text-slate-500'
                        }`}
                        style={active ? { background: `${ch.color}20`, borderColor: `${ch.color}40`, color: ch.color } : {}}>
                        <Icon className="w-3.5 h-3.5" /> {ch.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
            <div className="flex gap-3 p-5 border-t border-slate-800">
              <button onClick={() => setUploadModal(false)}
                className="flex-1 py-2.5 bg-slate-800 border border-slate-700 text-slate-300 rounded-xl text-sm hover:bg-slate-700">
                Cancelar
              </button>
              <button
                data-testid="confirm-upload-btn"
                onClick={uploadPost}
                disabled={!form.file}
                className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-[#00d4ff] text-[#030712] font-bold rounded-xl hover:bg-[#00bfea] disabled:opacity-40 transition-all text-sm"
              >
                <Upload className="w-4 h-4" /> Subir y programar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit post modal */}
      {editPost && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-[#0f1117] border border-slate-800 rounded-2xl w-full max-w-lg shadow-2xl">
            <div className="flex items-center justify-between p-5 border-b border-slate-800">
              <h3 className="font-bold text-white">Editar post</h3>
              <button onClick={() => setEditPost(null)} className="p-1.5 text-slate-500 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <input type="text" value={editPost.title || ''}
                onChange={e => setEditPost(p => ({ ...p, title: e.target.value }))}
                placeholder="Título"
                className="w-full bg-slate-800 border border-slate-700 text-white rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-[#00d4ff]/50"
              />
              <textarea value={editPost.caption || ''} rows={4}
                onChange={e => setEditPost(p => ({ ...p, caption: e.target.value }))}
                placeholder="Caption..."
                className="w-full bg-slate-800 border border-slate-700 text-white rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-[#00d4ff]/50 resize-none"
              />
              <input type="date" value={editPost.scheduled_date || ''}
                onChange={e => setEditPost(p => ({ ...p, scheduled_date: e.target.value }))}
                className="w-full bg-slate-800 border border-slate-700 text-white rounded-xl px-3 py-2.5 text-sm"
              />
              <select value={editPost.status || 'scheduled'}
                onChange={e => setEditPost(p => ({ ...p, status: e.target.value }))}
                className="w-full bg-slate-800 border border-slate-700 text-white rounded-xl px-3 py-2.5 text-sm">
                <option value="scheduled">Programado</option>
                <option value="published">Publicado</option>
                <option value="draft">Borrador</option>
              </select>
            </div>
            <div className="flex gap-3 p-5 border-t border-slate-800">
              <button onClick={() => setEditPost(null)}
                className="flex-1 py-2.5 bg-slate-800 border border-slate-700 text-slate-300 rounded-xl text-sm">
                Cancelar
              </button>
              <button onClick={() => updatePost(editPost.post_id, editPost)}
                className="flex-1 py-2.5 bg-[#00d4ff] text-[#030712] font-bold rounded-xl text-sm hover:bg-[#00bfea]">
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-slate-800 border border-slate-700 text-white text-sm px-5 py-3 rounded-xl shadow-xl z-50">
          {toast}
        </div>
      )}
    </div>
  );
}
